import type { MapDefinition, WaveDefinition } from '../game/model/types'

export type EditorTool = 'grass' | 'path' | 'spawn' | 'exit'

export const EDITOR_MIN_SIZE = 8
export const EDITOR_MAX_SIZE = 64
export const HISTORY_LIMIT = 80

export const DEFAULT_WAVE: WaveDefinition = {
  id: 'wave-1',
  groups: [{ enemy: 'normal', count: 1, interval: 1, delay: 0 }],
  completionReward: 0
}

export interface EditorDocument {
  map: MapDefinition
  /** Baseline fingerprint for dirty detection (JSON of map). */
  baselineJson: string
}

export function cloneMap(map: MapDefinition): MapDefinition {
  return structuredClone(map)
}

export function mapFingerprint(map: MapDefinition): string {
  return JSON.stringify(map)
}

export function createBlankMap(options?: {
  id?: string
  name?: string
  width?: number
  height?: number
  startingGold?: number
  startingLives?: number
}): MapDefinition {
  const width = options?.width ?? 18
  const height = options?.height ?? 32
  const rows = Array.from({ length: height }, () => 'G'.repeat(width))
  return {
    id: options?.id ?? 'untitled',
    name: options?.name ?? 'Untitled',
    schemaVersion: 1,
    width,
    height,
    rows,
    spawn: { x: 0, y: 0 },
    exit: { x: Math.max(0, width - 1), y: Math.max(0, height - 1) },
    path: [],
    startingGold: options?.startingGold ?? 250,
    startingLives: options?.startingLives ?? 20,
    waves: [structuredClone(DEFAULT_WAVE)]
  }
}

export function setTile(rows: string[], x: number, y: number, tile: 'G' | 'P'): string[] {
  if (y < 0 || y >= rows.length) return rows
  const row = rows[y]
  if (x < 0 || x >= row.length) return rows
  if (row[x] === tile) return rows
  const chars = [...row]
  chars[x] = tile
  const next = [...rows]
  next[y] = chars.join('')
  return next
}

export function resizeMap(
  map: MapDefinition,
  width: number,
  height: number
): MapDefinition {
  const rows = Array.from({ length: height }, (_, y) => {
    const source = map.rows[y] ?? ''
    let row = ''
    for (let x = 0; x < width; x += 1) {
      row += source[x] === 'P' ? 'P' : 'G'
    }
    return row
  })
  const clamp = (value: number, max: number) => Math.max(0, Math.min(max, value))
  return {
    ...map,
    width,
    height,
    rows,
    spawn: {
      x: clamp(map.spawn.x, width - 1),
      y: clamp(map.spawn.y, height - 1)
    },
    exit: {
      x: clamp(map.exit.x, width - 1),
      y: clamp(map.exit.y, height - 1)
    },
    path: []
  }
}
